import {
	exportTheme,
	useFlowTheme,
	useImportTheme,
	useUpdateFlowTheme
} from '@api/flows';
import AppHeader from '@components/AppNavigation/AppHeader';
import ExportImportButtons from '@components/ExportImportButtons/ExportImportButtons';
import { Theme } from '@descope/page-editor-components';
import { Box } from '@mui/material';
import { downloadAsFile } from '@utils/api';
import { useToast } from 'hooks';
import React, { useCallback } from 'react';
import { ToastSeverity } from 'types/toast';
import Footer from './Footer';
import { useStateWithUpdates } from './hooks';
import Skeleton from './Skeleton';
import Themes from './Themes';

export default () => {
	const { data, isLoading, isError } = useFlowTheme();
	const saveTheme = useUpdateFlowTheme();
	const importTheme = useImportTheme();
	const { addToast } = useToast();

	const [
		lightTheme,
		updateLightTheme,
		resetLightThemeState,
		isLightThemeStateDirty
	] = useStateWithUpdates<Theme>(data?.cssTemplate.light!);

	const [
		darkTheme,
		updateDarkTheme,
		resetDarkThemeState,
		isDarkThemeStateDirty
	] = useStateWithUpdates<Theme>(data?.cssTemplate.dark!);

	const onSave = useCallback(async () => {
		try {
			await saveTheme.mutateAsync({
				id: data?.id!,
				version: data?.version!,
				cssTemplate: {
					light: lightTheme,
					dark: darkTheme
				}
			});
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr('styleEditor.save.success.toastMessage')
			});
		} catch (e) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('styleEditor.save.error.toastMessage')
			});
		}
	}, [saveTheme, data?.id, data?.version, lightTheme, darkTheme, addToast]);

	const onCancel = useCallback(() => {
		resetLightThemeState();
		resetDarkThemeState();
	}, [resetDarkThemeState, resetLightThemeState]);

	const handleExport = useCallback(async () => {
		const exported = await exportTheme();
		downloadAsFile(exported, 'themes.json');
	}, []);

	return (
		<Box
			sx={{
				flexGrow: 1,
				backgroundColor: 'transparent',
				color: 'currentcolor'
			}}
			display="flex"
			flexDirection="column"
			overflow="hidden"
		>
			<Box
				display="flex"
				flexDirection="column"
				overflow="hidden"
				sx={{ flexGrow: 1, overflow: 'hidden' }}
			>
				<AppHeader translation="pages.flowStyleEditor.title" />
				{isError || isLoading ? (
					<Skeleton />
				) : (
					<Themes
						{...{ lightTheme, updateLightTheme, darkTheme, updateDarkTheme }}
					>
						<Box mr={1}>
							<ExportImportButtons
								onExport={handleExport}
								onImport={importTheme.mutateAsync}
								toastLabelPrefix="styleEditor.theme.import"
								question={intl.tr('styleEditor.theme.import.approve.question')}
								title={intl.tr('styleEditor.theme.import.approve.title')}
							/>
						</Box>
					</Themes>
				)}
			</Box>
			<Footer
				onSave={onSave}
				onCancel={onCancel}
				isSaving={saveTheme.isLoading}
				isDirty={isLightThemeStateDirty || isDarkThemeStateDirty}
			/>
		</Box>
	);
};
